import { useState, useEffect } from 'react';
import { Box, Paper, Stepper, Step, StepLabel, Button, Typography, Snackbar, Alert, FormControlLabel, Switch, IconButton, Divider } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ClientDetails from './components/ClientDetails';
import ProductDetails from './components/ProductDetails';
import ConsignerDetails from './components/ConsignerDetails';
import TransporterDetails from './components/TransporterDetails';
import ExportDetails from './components/ExportDetails';
import InquiryReview from './components/InquiryReview';
import { useAuth } from '../../context/AuthContext';
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from 'axios';

const steps = ['Client Details', 'Product Details', 'Dispatch Details', 'Review & Submit'];

// Utility function for deep merging objects
const deepMerge = (target, source) => {
    const output = { ...target };
    for (const key in source) {
        if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            output[key] = deepMerge(target[key] || {}, source[key]);
        } else {
            output[key] = source[key];
        }
    }
    return output;
};

const ProformaInvoiceMaster = ({ companyData, mode, connectionString, onPopupCancel }) => {
    const { user, selectedCompany } = useAuth();
    const userId = user?.UserId;
    const companyId = selectedCompany?.CompId || null;
    const AppRegID = selectedCompany?.AppRegID || null;
    let path = connectionString;

    const { clientId, proformaInvoiceId } = useParams();
    const { state } = useLocation();
    const navigate = useNavigate();

    const [activeStep, setActiveStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [consignerEnabled, setConsignerEnabled] = useState(true);
    const [transporterEnabled, setTransporterEnabled] = useState(true);
    const [exportEnabled, setExportEnabled] = useState(false);

    const handleNext = (stepData = {}) => {
        // console.log('Step data:', stepData);
        setFormData(prev => deepMerge(prev, stepData));
        setActiveStep(prev => prev + 1);
    };

    // Get selected Sale Order IDs from navigation state
    const soIds = state?.selectedSOIds || [];

    // ── on mount, fetch existing sale order data and seed formData ────────────────────────
    useEffect(() => {
        if (mode === 'proforma' && state?.selectedSOIds) {
            const soIds = state.selectedSOIds;
            if (soIds.length === 0) {
                setSnackbar({ open: true, message: 'No sale orders selected.', severity: 'error' });
                return;
            }
            const data = { clientId, soIds };
            // console.log(data);

            // Changed API endpoint from /api/challans to /api/fetchorders
            axios.post(`${path}/api/fetchorders`, data)
                .then(({ data }) => {
                    console.log('Fetched data:', data);

                    // Set the entire response as formData
                    setFormData(data);

                    // Show a success snackbar
                    // setSnackbar({ open: true, message: 'Sale order data fetched successfully!', severity: 'success' });
                })
                .catch(err => {
                    console.error('Failed to load sale order', err);
                    setSnackbar({ open: true, message: 'Failed to load Sale Order', severity: 'error' });
                });
        }
    }, [clientId]);

    useEffect(() => {
        if (mode === 'edit') {
            // Make sure proformaInvoiceId exists before calling
            if (!proformaInvoiceId) {
                console.error("proformaInvoiceId is missing!");
                return;
            }

            // Send GET request with ID in query params
            axios.get(`${path}/api/proforma-invoice-getinfo`, {
                params: { proformaInvoiceId }  // ← This is important
            })
                .then(res => {
                    console.log("Proforma Invoice Data:", res.data);
                    setFormData(res.data);
                    setConsignerEnabled(res.data.consigner !== null);
                    setTransporterEnabled(res.data.transporter !== null || res.data.consigner !== null);
                    setExportEnabled(res.data.export !== null);
                })
                .catch(err => {
                    console.error("Error fetching proforma invoice:", err);
                    if (err.response?.status === 400) {
                        setSnackbar({ open: true, message: 'Proforma Invoice ID is missing or invalid', severity: 'error' });
                    }
                });
        }
    }, [proformaInvoiceId]);

    const handleBack = () => setActiveStep(prev => prev - 1);

    const handleSubmit = async () => {
        try {
            const payload = { client: formData.client, product: formData.product, consigner: transporterEnabled ? formData.consigner : null, transporter: transporterEnabled ? formData.transporter : null, export: exportEnabled ? formData.export : null, clientId, companyId, userId, soIds, AppRegID };

            console.log(payload);

            if (mode === 'add' || mode === 'proforma') {
                // Changed API endpoint for creating proforma invoice from sale order
                await axios.post(`${path}/api/create-proforma-invoice-from-saleorder`, payload);
            }
            if (mode === 'edit') {
                // Changed API endpoint for updating proforma invoice
                await axios.put(`${path}/api/proforma-invoice-update/${proformaInvoiceId}`, payload);
            }
            setActiveStep(0);
            setFormData({});
            setSnackbar({ open: true, message: mode === 'add' || mode === 'proforma' ? 'Proforma Invoice created successfully!' : 'Proforma Invoice updated successfully!', severity: 'success', });
            setTimeout(() => {
                if (typeof onPopupCancel === "function") onPopupCancel();
                else navigate(-1);
            }, 600);

        } catch (error) {
            console.error(error);
            setSnackbar({ open: true, message: 'Failed to submit proforma invoice.', severity: 'error' });
        }
    };

    const handleCancel = () => {
        if (typeof onPopupCancel === "function") {
            onPopupCancel();
        } else {
            navigate(-1);
        }
    };

    // Sync activeStep with URL changes (e.g., browser back button).
    // Note: by the time popstate fires, the browser has ALREADY moved history
    // back one entry — calling handleCancel()/navigate(-1) here would pop a
    // SECOND entry on top of that, overshooting past the intended page. Just
    // reset local wizard state; the browser's own navigation handles the page.
    useEffect(() => {
        const handlePopState = () => {
            setActiveStep(0);
        };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    // ──────────────── Alt+C (Cancel) shortcut — works from any step ───────────
    // Attached here (the wizard's parent), not per-step, so it works regardless
    // of which step (Client Details / Product Details / Dispatch Details /
    // Review) is active — those step components don't each carry their own
    // cancel concept.
    // Attached to document in the capture phase so it fires regardless of which
    // field/dropdown currently has focus, including MUI popper/portal content.
    // Uses e.code (physical key position) instead of e.key so this also works on
    // macOS, where Option+letter often produces an accented/dead-key character
    // via e.key (e.g. Option+C → "ç") instead of the plain letter.
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (!e.altKey || e.code !== "KeyC") return;
            e.preventDefault();
            handleCancel();
        };

        document.addEventListener("keydown", handleGlobalKeyDown, true);
        return () => document.removeEventListener("keydown", handleGlobalKeyDown, true);
    }, [handleCancel]);

    const handleCloseSnackbar = () => setSnackbar(prev => ({ ...prev, open: false }));

    const renderStepHeader = (icon, title, control = null) => (
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box display="flex" alignItems="center">
                {activeStep > 0 && (
                    <IconButton onClick={handleBack} size="small" sx={{ mr: 1 }}>
                        <ArrowBack />
                    </IconButton>
                )}
                <Typography variant="h6" fontWeight="bold">{icon} {title}</Typography>
            </Box>
            {control}
        </Box>
    );

    const disablePartyName =
        Array.isArray(formData?.product?.tableData) &&
        formData.product.tableData.some(
            row => row?.soId != null || row?.soDtsID != null
        );

    // const getStepComponent = (step) => {
    //     switch (step) {
    //         case 0:
    //             return (
    //                 <>
    //                     {renderStepHeader('1️⃣', 'Client Details')}
    //                     <ClientDetails connectionString={connectionString} mode={mode} initialData={formData.client || {}} onNext={(data) => handleNext({ client: data })} />
    //                 </>
    //             );

    //         case 1:
    //             return (
    //                 <>
    //                     {renderStepHeader('2️⃣', 'Product Details')}
    //                     <ProductDetails connectionString={connectionString} initialData={formData.product || {}} onNext={(data) => handleNext({ product: data })} companyData={companyData} clientData={formData.client || {}} soIds={soIds} />
    //                 </>
    //             );

    //         case 2:
    //             return (
    //                 <>
    //                     {renderStepHeader('3️⃣', 'Consigner & Consignee',
    //                         <FormControlLabel control={ <Switch checked={consignerEnabled} onChange={e => setConsignerEnabled(e.target.checked)} /> } label="Enable" />
    //                     )}
    //                     {consignerEnabled ? (
    //                         <ConsignerDetails connectionString={connectionString} mode={mode} clientData={formData.client || {}} initialData={formData.consigner || {}} onNext={data => handleNext({ consigner: data })} onBack={handleBack} companyData={companyData} />
    //                     ) : (
    //                         <Box mt={2}>
    //                             <Typography color="text.secondary">Consigner details are skipped.</Typography>
    //                             <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
    //                         </Box>
    //                     )}
    //                 </>
    //             );

    //         case 3:
    //             return (
    //                 <>
    //                     {renderStepHeader('4️⃣', 'Dispatch Details',
    //                         <FormControlLabel control={ <Switch checked={transporterEnabled} onChange={e => setTransporterEnabled(e.target.checked)} /> } label="Enable"
    //                         />
    //                     )}
    //                     {transporterEnabled ? (
    //                         <TransporterDetails connectionString={connectionString} mode={mode} initialData={formData.transporter || {}} onNext={data => handleNext({ transporter: data })} onBack={handleBack} companyData={companyData} />
    //                     ) : (
    //                         <Box mt={2}>
    //                             <Typography color="text.secondary">Dispatch details are skipped.</Typography>
    //                             <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
    //                         </Box>
    //                     )}
    //                 </>
    //             );

    //         case 4:
    //             return (
    //                 <>
    //                     {renderStepHeader('5️⃣', 'Export Details',
    //                         <FormControlLabel control={ <Switch checked={exportEnabled} onChange={e => setExportEnabled(e.target.checked)} /> } label="Enable" />
    //                     )}
    //                     {exportEnabled ? (
    //                         <ExportDetails connectionString={connectionString} mode={mode} initialData={formData.export || {}} onNext={data => handleNext({ export: data })} onBack={handleBack} companyData={companyData} />
    //                     ) : (
    //                         <Box mt={2}>
    //                             <Typography color="text.secondary">Export details are skipped.</Typography>
    //                             <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
    //                         </Box>
    //                     )}
    //                 </>
    //             );

    //         case 5:
    //             return (
    //                 <InquiryReview formData={formData} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} connectionString={connectionString} mode={mode} selectedCompany={selectedCompany} />
    //             );

    //         default:
    //             return null;
    //     }
    // };


    const getStepComponent = (step) => {
        switch (step) {
            case 0:
                return (
                    <>
                        {renderStepHeader('1️⃣', 'Client Details')}
                        <ClientDetails connectionString={connectionString} mode={mode} initialData={formData.client || {}} onNext={(data) => handleNext({ client: data })} disablePartyName={disablePartyName} />
                    </>
                );

            case 1:
                return (
                    <>
                        {renderStepHeader('2️⃣', 'Product Details')}
                        <ProductDetails connectionString={connectionString} initialData={formData.product || {}} onNext={(data) => handleNext({ product: data })} companyData={companyData} clientData={formData.client || {}} soIds={soIds} mode={mode}
                            onRowsChange={(rows) => {
                                setFormData(prev => ({
                                    ...prev,
                                    product: {
                                        ...(prev.product || {}),
                                        tableData: rows,
                                    }
                                }));
                            }} />
                    </>
                );

            /* COMBINED STEP (Step 2 consignee and despatch details) */
            case 2:
                return (
                    <>
                        {renderStepHeader('3️⃣', 'Dispatch Details', (<FormControlLabel control={<Switch checked={transporterEnabled} onChange={e => setTransporterEnabled(e.target.checked)} />} label="Enable" />))}
                        {transporterEnabled ? (
                            <>
                                <Box sx={{ display: 'flex', flexDirection: "column", gap: 4 }}>
                                    <ConsignerDetails
                                        clientData={formData.client || {}}
                                        initialData={formData.consigner}
                                        connectionString={connectionString}
                                        onChange={(data) =>
                                            setFormData(prev => ({ ...prev, consigner: data }))
                                        }
                                        companyData={companyData}
                                    />

                                    <TransporterDetails
                                        initialData={formData.transporter || {}}
                                        connectionString={connectionString}
                                        onChange={(data) =>
                                            setFormData(prev => ({
                                                ...prev,
                                                transporter: data
                                            }))

                                        }
                                        companyData={companyData}
                                    />
                                </Box>


                                {/* ✅ SINGLE NEXT BUTTON */}
                                <Box mt={3} display="flex" justifyContent="flex-end">
                                    <Button
                                        variant="contained"
                                        // disabled={
                                        //     !formData?.consigner?.shipFrom ||
                                        //     !formData?.consigner?.shipTo ||
                                        //     !formData?.transporter?.transporterName
                                        // }
                                        onClick={() => {
                                            handleNext({
                                                consigner: formData.consigner,
                                                transporter: formData.transporter
                                            });
                                        }}
                                    >
                                        Next
                                    </Button>
                                </Box>

                            </>
                        ) : (
                            <Box mt={2}>
                                <Typography color="text.secondary">Dispatch details are skipped.</Typography>
                                <Button variant="contained" onClick={() => handleNext({})}>Continue</Button>
                            </Box>
                        )}

                    </>
                );

            case 3:
                return (
                    <InquiryReview formData={formData} onBack={handleBack} onSubmit={handleSubmit} onEditStep={(stepIndex) => setActiveStep(stepIndex)} connectionString={connectionString} mode={mode} selectedCompany={selectedCompany} />
                );

            default:
                return null;
        }
    };
    return (
        <Box maxWidth="lg" mx="auto" px={2} py={3} pb={0} >
            {/* <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600, mt: -0.5, mb: 1 }}>
                {mode === 'edit' ? 'Edit Proforma Invoice' : 'Add New Proforma Invoice'}
            </Typography> */}
            <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 0.6, mt: -1.5 }}>
                {/* Back arrow — mobile/tablet only */}
                <IconButton onClick={() => navigate(-1)} sx={{ position: 'absolute', left: 0, display: { xs: 'flex', md: 'none' }, color: 'text.secondary', p: 0.5, }} >
                    <ArrowBackIcon fontSize="small" />
                </IconButton>

                <Typography variant="h5" sx={{ textAlign: 'center', fontSize: { xs: '1.25rem', sm: '1.5rem' }, fontWeight: 600 }}>
                    {mode === 'edit' ? 'Edit Proforma Invoice' : 'Add New Proforma Invoice'}
                </Typography>
            </Box>
            <Paper elevation={4} sx={{ borderRadius: 3, p: 1.5 }}>
                <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                <Divider sx={{ mb: 0.5 }} />
                {getStepComponent(activeStep)}
            </Paper>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} variant="filled">
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default ProformaInvoiceMaster;